import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx and tailwind-merge for optimal class handling
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format date to readable string
 */
export function formatDate(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format date and time
 */
export function formatDateTime(date: Date | string): string {
  const d = new Date(date)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Truncate text to specified length
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text
  return text.slice(0, length) + '...'
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Calculate age from birthday
 * @param birthday - Date string or Date object
 * @returns Age in years, or null if birthday is not provided
 */
export function calculateAge(birthday: Date | string | null | undefined): number | null {
  if (!birthday) return null

  const birthDate = new Date(birthday)
  const today = new Date()

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  // Adjust age if birthday hasn't occurred this year yet
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age >= 0 ? age : null
}

/**
 * Format address from multiple components
 * @param street - Street name/house number
 * @param barangay - Barangay
 * @param city - City/Municipality
 * @param province - Province
 * @param postalCode - Postal code
 * @returns Formatted address string
 */
export function formatAddress(
  street?: string | null,
  barangay?: string | null,
  city?: string | null,
  province?: string | null,
  postalCode?: string | null
): string {
  const parts: string[] = []

  if (street) parts.push(street)
  if (barangay) parts.push(barangay)
  if (city) parts.push(city)
  if (province) parts.push(province)
  if (postalCode) parts.push(postalCode)

  return parts.length > 0 ? parts.join(', ') : 'N/A'
}
